import { ReactElement, useMemo } from 'react';

import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { ScrollToTop } from '@/components/atoms';
import { HiddenSeoHeading } from '@/components/functional/HiddenSEOHeading/HiddenSEOHeading';
import { DefaultLayout, Footer } from '@/components/layouts';
import { AllArticlesSection } from '@/components/sections/blog/AllArticles';
import { LetsBuildSomethingSection, Newsletter } from '@/components/sections/common';
import { OurGuidesSection } from '@/components/sections/library/OurGuidesSection';
import { SectionWrapper } from '@/components/wrappers';
import { PropsBuilder, getPublicationState } from '@/graphql/api';
import {
  useGetArticleCardsQuery,
  useGetArticleCategoriesQuery,
  useGetGlobalQuery,
  useGetLibrariesQuery,
  useGetMenuQuery,
  useGetResourcesPageQuery,
} from '@/graphql/generated';
import { useLocale } from '@/hooks';
import { NextPageWithLayout } from '@/pages/_app';
import { DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';

const ARTICLES_PER_PAGE = 21;

const Blog: NextPageWithLayout<{ preview: boolean }> = ({ preview }) => {
  const locale = useLocale();
  const ebooks = useGetLibrariesQuery({ locale }).data?.libraries?.data;

  const allBooks = useMemo(() => ebooks?.map((b) => b.attributes!) || [], [ebooks]);

  return (
    <>
      <SectionWrapper id="allResourcesPage">
        <HiddenSeoHeading heading="Blog" />
        <AllArticlesSection isPreview={preview} articlesPerPage={ARTICLES_PER_PAGE} />
        <Newsletter />
        <OurGuidesSection ebooks={allBooks} showMore={true} />
        <LetsBuildSomethingSection overflow={false} />
      </SectionWrapper>
      <ScrollToTop />
    </>
  );
};

Blog.getLayout = (page: ReactElement) => {
  const { metadata, preview, menu } = page.props;

  return (
    <DefaultLayout metadata={metadata} menu={menu} preview={preview}>
      {page}
      <Footer menu={menu} />
    </DefaultLayout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { preview = false, locale } = context;

  const publicationState = getPublicationState(preview);
  const articlesQueryVariables = {
    publicationState,
    page: 1,
    pageSize: ARTICLES_PER_PAGE,
    sort: ['creationDate:desc'],
    title: '',
  };

  const propsBuilder = new PropsBuilder();

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetLibrariesQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetArticleCategoriesQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetArticleCardsQuery, {
    variables: {
      ...articlesQueryVariables,
      locale,
    },
  });
  propsBuilder.withQuery(useGetResourcesPageQuery, {
    getMetadata: (data) => data.resourcesPage?.data?.attributes?.metadata,
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetMenuQuery, {
    getMenu: (data) => data.menu,
  });

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON, LOCALES.ARTICLES]);

  return {
    props: {
      ...(await propsBuilder.build(lang)),
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default Blog;
